// Shared state for the Broadcasts page: language + copy, styles, screen size,
// picker options, templates, audiences, history counts and the AlertModal
// helper — loaded once, used by every tab.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";
import { broadcastApi } from "./api";
import { getBroadcastStyles } from "./styles";
import { text } from "./constants/broadcastText";

const BroadcastPageContext = createContext(null);

const getScreenSize = () => {
  const width = window.innerWidth;
  return { width, isMobile: width <= 768, isTablet: width > 768 && width <= 1024 };
};

export const BroadcastPageProvider = ({ children }) => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext) || { language: "en" };
  const lang = language === "ar" ? "ar" : "en";
  const t = text[lang];
  const isRTL = lang === "ar";

  const [screenSize, setScreenSize] = useState(getScreenSize);
  useEffect(() => {
    const onResize = () => setScreenSize(getScreenSize());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const s = useMemo(() => getBroadcastStyles(screenSize, theme, lang), [screenSize, theme, lang]);

  const [options, setOptions] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [counts, setCounts] = useState({ scheduled: null, sent: null });
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ open: false, title: "", message: "", variant: "info" });

  // Same AlertModal pattern as Departments: errors and confirmations of a
  // finished action both show in the shared modal.
  const notify = useCallback(
    (type, message) => {
      setAlert({ open: true, title: type === "error" ? t.error : t.success, message, variant: type === "error" ? "error" : "success" });
    },
    [t]
  );
  const closeAlert = useCallback(() => setAlert((a) => ({ ...a, open: false })), []);

  const refreshTemplates = useCallback(async () => {
    try {
      setTemplates((await broadcastApi.listTemplates()) || []);
    } catch (e) {
      notify("error", `${t.loadFailed.templates}: ${e.message}`);
    }
  }, [notify, t]);

  const refreshAudiences = useCallback(async () => {
    try {
      setAudiences((await broadcastApi.listAudiences()) || []);
    } catch (e) {
      notify("error", `${t.loadFailed.audiences}: ${e.message}`);
    }
  }, [notify, t]);

  const refreshOptions = useCallback(async () => {
    try {
      setOptions(await broadcastApi.targetOptions());
    } catch (e) {
      notify("error", `${t.loadFailed.options}: ${e.message}`);
      setOptions({ roles: [], clients: [], departments: [], adminLevels: [], groups: [] });
    }
  }, [notify, t]);

  // Stat cards: totals come from the history endpoint's pagination.
  const refreshCounts = useCallback(async () => {
    try {
      const [scheduled, sent] = await Promise.all([
        broadcastApi.list({ status: "SCHEDULED", limit: 1 }),
        broadcastApi.list({ status: "SENT", limit: 1 }),
      ]);
      setCounts({ scheduled: scheduled?.pagination?.total ?? 0, sent: sent?.pagination?.total ?? 0 });
    } catch {
      setCounts({ scheduled: null, sent: null });
    }
  }, []);

  // Load once on mount; a language switch must not refetch.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await Promise.all([refreshTemplates(), refreshAudiences(), refreshOptions(), refreshCounts()]);
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    theme,
    language: lang,
    isRTL,
    t,
    s,
    screenSize,
    options,
    templates,
    audiences,
    counts,
    loading,
    alert,
    notify,
    closeAlert,
    refreshTemplates,
    refreshAudiences,
    refreshOptions,
    refreshCounts,
  };
  return React.createElement(BroadcastPageContext.Provider, { value }, children);
};

export const useBroadcastPage = () => {
  const ctx = useContext(BroadcastPageContext);
  if (!ctx) throw new Error("useBroadcastPage must be used inside BroadcastPageProvider");
  return ctx;
};
